// A talking face's moving parts over a line: when it blinks, and how it feels.
import type { Line } from "../model/episode";
import type { Emotion } from "../model/face";
import { hash, rng } from "./rng";
import { FPS } from "./timeline";

const BLINK = 3 / FPS;
const blinkCache = new Map<string, number[]>();

// Blinks every 2 to 6 seconds, the same for a given seed and character.
function blinkTimes(seed: number, id: string) {
  const key = `${seed}:${id}`;
  let times = blinkCache.get(key);
  if (!times) {
    const r = rng(seed ^ hash(id));
    times = [];
    for (let t = 0.5 + r() * 2; t < 600; t += 2 + r() * 4) times.push(t);
    blinkCache.set(key, times);
  }
  return times;
}

// Whether `character` has their eyes shut at `t` seconds into `line` (or, with no line, of wall time).
export function isBlinking(line: Line | null, character: string, t: number, clock: number) {
  const tt = (((line ? t : clock) % 600) + 600) % 600;
  return blinkTimes(line ? hash(line.id) : 1, character).some(b => tt >= b && tt < b + BLINK);
}

// How `character` feels at `t` seconds into `line`. Only the speaker shows a feeling; everyone else is neutral.
export function emotionAt(line: Line | null, character: string, t: number): Emotion {
  if (!line || line.speaker !== character) return "neutral";
  return line.emotion;
}

// Whether eyes are shut at `clock` seconds, blinking on a schedule seeded by `seed` and `id` (timeline faces: the face clip and the character).
export const blinkingAt = (seed: number, id: string, clock: number) => {
  const tt = ((clock % 600) + 600) % 600;
  return blinkTimes(seed, id).some(b => tt >= b && tt < b + BLINK);
};
